import type { CardholderOnboardingTranslations } from "@/shared/lang/interfaces/cardholderOnboarding";

import type {
  CardholderBoardItem,
  CardholderBucket,
  CardholderNextAction,
  CardholderSkipReason,
} from "../types/cardholderOnboarding.types";

type CopyKey = keyof CardholderOnboardingTranslations;

export type TrackStepState = "done" | "current" | "blocked" | "failed" | "pending";

export interface CardholderTrack {
  registration: TrackStepState;
  verification: TrackStepState;
  card: TrackStepState;
}

/** Order of the filter strip: what needs a hand first, what is settled last. */
export const BUCKET_ORDER: CardholderBucket[] = [
  "ready",
  "paused",
  "needs_verification",
  "needs_data",
  "registration_failed",
  "in_progress",
  "enabled",
  "closed",
];

export const BUCKET_LABEL_KEYS: Record<CardholderBucket, CopyKey> = {
  ready: "bucket_ready",
  paused: "bucket_paused",
  needs_verification: "bucket_needs_verification",
  needs_data: "bucket_needs_data",
  registration_failed: "bucket_registration_failed",
  in_progress: "bucket_in_progress",
  enabled: "bucket_enabled",
  closed: "bucket_closed",
};

export const BUCKET_ACCENT: Record<
  CardholderBucket,
  "primary" | "success" | "warning" | "info" | "danger" | "neutral"
> = {
  ready: "primary",
  paused: "warning",
  needs_verification: "warning",
  needs_data: "warning",
  registration_failed: "danger",
  in_progress: "info",
  enabled: "success",
  closed: "neutral",
};

export const ACTION_LABEL_KEYS: Record<Exclude<CardholderNextAction, "none">, CopyKey> = {
  send: "action_send",
  resume: "action_resume",
  link_verification: "action_link_verification",
  sync_verification: "action_sync_verification",
  complete_data: "action_complete_data",
  retry_registration: "action_retry_registration",
};

export const SKIP_REASON_LABEL_KEYS: Record<CardholderSkipReason, CopyKey> = {
  already_enabled: "skip_already_enabled",
  already_rejected: "skip_already_rejected",
  no_kyc_record: "skip_no_kyc_record",
  kyc_not_verified: "skip_kyc_not_verified",
  no_kyc_applicant: "skip_no_kyc_applicant",
  missing_identity_fields: "skip_missing_identity_fields",
  missing_client_email: "skip_missing_client_email",
  limit_reached: "skip_limit_reached",
  concurrent_conflict: "skip_concurrent_conflict",
  error: "skip_error",
};

export function isQueueAction(action: CardholderNextAction): action is "send" | "resume" {
  return action === "send" || action === "resume";
}

/** Rows that can join a bulk send: only where the provider onboards one by one and the user may write. */
export function isSelectableRow(item: CardholderBoardItem, canQueue: boolean): boolean {
  return canQueue && isQueueAction(item.next_action);
}

/**
 * The three steps a person crosses to hold a card, read off the board row: registered as a cardholder,
 * verified, then onboarded at the card provider. A step after one that is not done is never "done" on its own
 * account — it shows as pending, so the track points at the one place the person is held.
 */
export function cardholderTrack(item: CardholderBoardItem): CardholderTrack {
  const registration: TrackStepState = item.identity_status === "ACTIVE" ? "done" : "failed";

  const verified = item.kyc_status === "VERIFIED" && item.kyc_linked;
  const verification: TrackStepState =
    registration !== "done"
      ? "pending"
      : verified
        ? "done"
        : item.kyc_status === "REJECTED"
          ? "failed"
          : "blocked";

  let card: TrackStepState;
  if (item.card_stage === "enabled") card = "done";
  else if (item.card_stage === "rejected" || item.bucket === "closed") card = "failed";
  else if (verification !== "done") card = "pending";
  else if (item.card_stage === "paused" || item.bucket === "needs_data") card = "blocked";
  else if (item.card_stage === "in_progress" || item.card_stage === "in_review") card = "current";
  else card = "pending";

  return { registration, verification, card };
}

/** The sentence under the track: where the person is held, in one line. */
export function cardholderHeadlineKey(item: CardholderBoardItem): CopyKey {
  switch (item.bucket) {
    case "ready":
      return item.next_action === "resume" ? "headline_ready_resume" : "headline_ready";
    case "paused":
      return item.pause_reason ? "headline_paused_reason" : "headline_paused";
    case "needs_verification":
      if (item.next_action === "sync_verification") return "headline_verification_not_approved";
      if (item.next_action === "link_verification") return "headline_verification_missing";
      return item.blocked_reason === "no_kyc_record"
        ? "headline_verification_other_service"
        : "headline_verification_incomplete";
    case "needs_data":
      return item.blocked_reason === "missing_client_email"
        ? "headline_missing_email"
        : "headline_missing_identity_fields";
    case "registration_failed":
      return "headline_registration_failed";
    case "in_progress":
      return item.card_stage === "in_review" ? "headline_in_review" : "headline_in_progress";
    case "enabled":
      return "headline_enabled";
    case "closed":
      if (item.identity_status === "REJECTED") return "headline_identity_rejected";
      if (item.card_stage === "rejected") return "headline_card_rejected";
      if (item.type === "business") return "headline_business";
      return "headline_closed";
  }
}

export function cardholderDocument(item: CardholderBoardItem): string | null {
  if (!item.identification_number) return null;
  return item.identification_type
    ? `${item.identification_type} ${item.identification_number}`
    : item.identification_number;
}
